import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { PopupModal } from "../../../components/TopBar/PopupModal/PopupModal";
import { ReactComponent as MenuIcon } from "../../../assets/svgIcon/shipment/Menu.svg";
import { ButtonSpinner } from "../../../../../components/atoms/Button/Button.styles";

/**
 * ListActionBurgerMenu Component
 *
 * Provides a standardized burger menu (3 horizontal bars) for list page action bars.
 * Renders into the right-side slot of OptionsForView via portal when available,
 * or falls back to an inline list item in isolated environments (e.g. tests).
 *
 * Key features:
 * - Keeps menu open when an action is clicked (does not auto-dismiss).
 * - Displays inline ButtonSpinner when an action is loading (isLoading === true).
 * - Keeps ineligible items permanently visible in a disabled state with tooltips.
 *
 * @param {Object} props
 * @param {Array<{
 *   key?: string,
 *   label: React.ReactNode,
 *   onClick: Function,
 *   disabled?: boolean,
 *   title?: string,
 *   isLoading?: boolean,
 *   isDanger?: boolean,
 * }>} props.items - Actions to display inside the dropdown.
 * @param {string} [props.slotId="options-right-burger-slot"] - Target DOM ID for the portal.
 */
export const ListActionBurgerMenu = ({ items = [], slotId = "options-right-burger-slot" }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [slot, setSlot] = useState(null);

  useLayoutEffect(() => {
    setSlot(document.getElementById(slotId));
  }, [slotId]);

  if (!items || items.length === 0) {
    return null;
  }

  const content = (
    <BurgerMenuContainer>
      <span
        className="menuIcon"
        onMouseDown={e => {
          if (isOpen) {
            e.stopPropagation();
          }
        }}
        onClick={() => setIsOpen(prev => !prev)}
        onKeyDown={e => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setIsOpen(prev => !prev);
          }
        }}
        title="More actions"
        aria-label="More actions"
        aria-haspopup="true"
        aria-expanded={isOpen}
        role="button"
        tabIndex={0}
      >
        <MenuIcon />
      </span>

      {isOpen && (
        <PopupModal styleOfModal={styleOfPopup} closeModal={() => setIsOpen(false)}>
          <BurgerMenuDropdownCss>
            <ul role="menu">
              {items.map((item, index) => {
                const itemKey = item.key || (typeof item.label === "string" ? item.label : index);
                const isDisabled = Boolean(item.disabled || item.isLoading);

                return (
                  <li
                    key={itemKey}
                    role="menuitem"
                    className={`optionItem ${isDisabled ? "disableItem" : ""} ${item.isDanger ? "dangerItem" : ""}`}
                    title={item.title || ""}
                    aria-disabled={isDisabled}
                    aria-busy={Boolean(item.isLoading)}
                    onClick={e => {
                      if (isDisabled) return;
                      // Trigger action without closing menu (keeps menu open)
                      item.onClick?.(e);
                    }}
                  >
                    <span className="itemContent">
                      {item.isLoading && <ButtonSpinner aria-hidden="true" />}
                      <span className="itemLabel">{item.label}</span>
                    </span>
                  </li>
                );
              })}
            </ul>
          </BurgerMenuDropdownCss>
        </PopupModal>
      )}
    </BurgerMenuContainer>
  );

  if (slot) {
    return createPortal(content, slot);
  }

  return <li>{content}</li>;
};

const BurgerMenuContainer = styled.div`
  position: relative;
  display: inline-flex;
  align-items: center;

  > .menuIcon {
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

export const BurgerMenuDropdownCss = styled.div`
  display: flex;
  padding: 4px 12px;
  flex-direction: column;
  align-items: flex-start;
  width: max-content;
  min-width: 180px;
  box-sizing: border-box;
  white-space: nowrap;
  border-radius: 4px;
  border: 1px solid var(--PEARL, #ecf1f4);
  background-color: var(--Conditional-pop-over, #fff);
  box-shadow:
    0px 3px 6px -4px rgba(0, 0, 0, 0.12),
    0px 6px 16px 0px rgba(0, 0, 0, 0.08),
    0px 9px 28px 8px rgba(0, 0, 0, 0.05);

  ul {
    width: 100%;
    margin: 0;
    padding: 0;
    list-style: none;
    white-space: nowrap;
  }

  ul > .optionItem {
    padding: 6px 12px;
    color: var(--DARK-GREY, #0e0e2c);
    font-weight: 400;
    font-size: 14px;
    line-height: 22px;
    border-radius: 4px;
    user-select: none;
    white-space: nowrap;
    box-sizing: border-box;
    transition: background-color 0.15s ease, color 0.15s ease;

    .itemContent {
      display: inline-flex;
      align-items: center;
      gap: 8px;
      width: 100%;
      white-space: nowrap;
    }

    .itemLabel {
      white-space: nowrap;
    }

    &:hover {
      background-color: var(--MIST, #f7f8f9);
      color: #1249bf;
      cursor: pointer;
    }

    &.dangerItem:hover {
      color: var(--Danger, #cf324a);
      background-color: #fff1f0;
    }
  }

  ul > .disableItem {
    cursor: not-allowed !important;
    color: rgba(0, 0, 0, 0.25) !important;

    &:hover {
      background-color: transparent !important;
      color: rgba(0, 0, 0, 0.25) !important;
    }
  }
`;

export const styleOfPopup = {
  position: "absolute",
  top: "calc(100% + 8px)",
  right: 0,
  zIndex: 1050,
  width: "max-content",
};
